import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCirclePlus,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import HelpButton from "../HelpContent/HelpButton";
import MyIcon from "../Common/MyIcon";
import UsersIcon from "../../assets/icons/Icon_Library/application icons/settings/users_roles.svg2";
import MySearch from "../Common/MySearch";
import MyPagination from "../Common/MyPagination";
import {
  useDeleteUserRolesDataMutation,
  useGetUserRolesTableDataQuery,
} from "../store/slices/settingsSlice.ts";
import { isUserPermitted } from "../helpers/pageProps";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export default function ManageUsersRoles({ collapsed, setCollapsed }) {
  const confirmDialog = useConfirmDialog();
  const [search, setSearch] = useState();
  const [debounsedSearch, setDebounsedSearch] = useState("");
  const [order, setOrder] = useState("asc");
  const [limit, setLimit] = useState(12);
  const [offset, setOffset] = useState(0);

  const { data: { rows: data, total } = { rows: [], total: 0 } } =
    useGetUserRolesTableDataQuery({
      limit,
      offset,
      order,
      search: debounsedSearch,
    });

  const [deleteRole] = useDeleteUserRolesDataMutation();

  const manageUsersRoles = data.map((d) => d.data);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebounsedSearch(search);
      setOffset(0);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  return (
    <div>
      <Row>
        <Col>
          <div className="bg-purple-gradient-darker-reverse rounded-4 px-3 mb-2 py-2">
            <div className="d-flex justify-content-between">
              <h5 className=" d-flex align-items-center">
                <MyIcon Icon={UsersIcon} className="me-2" />
                <span className="ms-2">
                  <span className="fw-bold me-2"> Manage Users Roles:</span>
                  <HelpButton helpKey="settings-users-manage-users-roles" />
                  <div className="small text-secondary">
                    Roles manage security access by grouping permissions,
                    allowing users to be assigned appropriate system access.
                  </div>
                </span>
              </h5>
              <div className=" d-flex align-items-center gap-2">
                <div>
                  <MySearch
                    placeholder="Search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                  />
                </div>
                {isUserPermitted("edit_permissions") && (
                  <div className="d-flex align-items-center px-2">
                    <Link
                      to="/settings/users/role/new"
                      className="btn btn-colored-reverse rounded-pill fw-bold border-0  "
                    >
                      <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
                      Add New Role
                      <HelpButton helpKey="settings-manage-users-roles-add-new-role" iconClassName="text-white" />
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-3 mb-4 my-2">
              <div
                className="table-responsive"
                style={{
                  maxHeight: collapsed ? "200px" : "none",
                  overflowY: "auto",
                  transition: "height 0.3s ease",
                }}
              >
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Role Name</th>
                      <th>Products</th>
                      <th>Product Access </th>
                      <th>Edit Roles</th>
                      <th>Remove Roles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manageUsersRoles.map((m) => (
                      <tr key={m.id}>
                        <td>{m.id}</td>
                        <td>{m.name}</td>
                        <td>
                          {m.clients.map((c) => (
                            <span className="badge bg-success">
                              {c.client_name}
                            </span>
                          ))}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{m.type}</span>
                        </td>

                        <td>
                          {isUserPermitted("edit_permissions") ? (
                            <Link
                              to={`/settings/users/role/${m.id}`}
                              className="btn btn-secondary btn-sm rounded-pill fw-bold"
                            >
                              <FontAwesomeIcon
                                icon={faPen}
                                className="me-2 text-white"
                              />
                              <span className="text-white">Edit</span>
                            </Link>
                          ) : (
                            <span
                              className="text-secondary"
                              title="You do not have enough permissions to access this feature"
                            >
                              No Access
                            </span>
                          )}
                        </td>
                        <td>
                          {isUserPermitted("edit_permissions") ? (
                            <button
                              type="button"
                              className="btn btn-secondary rounded-pill fw-bold  btn-sm "
                              onClick={async () => {
                                if (
                                  !(await confirmDialog(
                                    "Delete role",
                                    `Are you sure you want to delete this role: "${m.name}"?`
                                  ))
                                ) {
                                  return;
                                }
                                const res = await deleteRole(m.id);
                                if (res.data) {
                                  toast.success("Deleted Successfully ");
                                } else {
                                  toast.error("Something went wrong");
                                }
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="me-2"
                              />
                              Delete
                            </button>
                          ) : (
                            <span
                              className="text-secondary"
                              title="You do not have enough permissions to access this feature"
                            >
                              No Access
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="btn btn-link p-0 mt-2"
              >
                {collapsed ? "Show More" : "Show Less"}
              </button>
              <MyPagination
                key={debounsedSearch}
                total={total}
                onPaginate={(page, perPage) => {
                  setLimit(perPage);
                  setOffset(page * perPage);
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
